// Shared settings for the city portal: rollout stages, services, staff roles,
// contract pricing helpers and the CSV import types.
// functions/src/index.ts keeps the same stage names and import checks, so if
// you change a list here, change it there too.
import { PRICING } from "@/config/site";

// ---------- Rollout stages ----------
// Stored on customers/{cityId}.appProgress as the `name`.
export const CITY_STAGES = [
  { name: "Not Started", blurb: "Contract is being finalized." },
  { name: "Kickoff", blurb: "Contract signed. Kickoff call with the Treasurer's office." },
  { name: "Data Import", blurb: "Bill files, open parking tickets and staff list uploaded and checked." },
  { name: "Setup & Training", blurb: "Departments, fees and receipts configured. Staff trained." },
  { name: "Testing", blurb: "Test payments run with the city's office before residents can pay." },
  { name: "Live", blurb: "Residents can pay online. The city can send change requests." },
];
export const STAGE_NAMES = CITY_STAGES.map((s) => s.name);
export const LIVE_STAGE = "Live";

// Older records may still hold the Corporate Connectionz stage names.
const LEGACY_STAGES = { Designing: "Kickoff", Developing: "Setup & Training", Completed: "Live" };

export function normalizeStage(stage) {
  if (!stage) return "Not Started";
  return LEGACY_STAGES[stage] || (STAGE_NAMES.includes(stage) ? stage : "Not Started");
}

export function stageIndex(stage) {
  return STAGE_NAMES.indexOf(normalizeStage(stage));
}

export function stagePercent(stage) {
  return Math.round((stageIndex(stage) / (STAGE_NAMES.length - 1)) * 100);
}

export function stageTone(stage) {
  const i = stageIndex(stage);
  if (i === STAGE_NAMES.length - 1) return "green";
  if (i === 0) return "";
  return "amber";
}

// ---------- What the city will collect / use ----------
export const CITY_SERVICES = [
  "Real estate tax",
  "Personal property tax",
  "Motor vehicle excise",
  "Water & sewer",
  "Trash & recycling",
  "Parking tickets",
  "Permits & licenses",
  "Dog licenses",
  "Report a problem (311)",
  "Records requests",
  "Hearings",
  "Emergency alerts",
];

export const CITY_STAFF_ROLES = [
  "Treasurer/Collector",
  "Assistant Treasurer/Collector",
  "Clerk",
  "Accountant/Finance",
  "Parking Clerk",
  "Police",
  "DPW",
  "IT",
  "Administrator",
  "View only",
];

export const CONTACT_TITLES = [
  "Treasurer/Collector",
  "Assistant Treasurer/Collector",
  "Town/City Clerk",
  "Town/City Manager",
  "Mayor / Select Board",
  "Finance Director",
  "Other",
];

// ---------- Contract pricing ----------
const dollars = (s) => {
  const n = Number(String(s || "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export const PRICE_TIERS = PRICING.tiers.map((t) => ({
  label: t.size,
  setupFee: dollars(t.setup),
  yearlyFee: dollars(t.yearly),
  custom: !dollars(t.yearly),
}));

// Population (number) -> index into PRICE_TIERS.
export function suggestTier(population) {
  const p = Number(population) || 0;
  if (!p) return 1;
  if (p < 10000) return 0;
  if (p < 50000) return 1;
  if (p < 150000) return 2;
  return 3;
}

export const PLAN_TYPES = [
  { value: "subscription", label: "Yearly subscription", hint: "Setup fee plus a yearly fee. Residents pay the per-payment fee." },
  { value: "fee_only", label: "Fee-only (no budget)", hint: `No yearly fee. ${PRICING.feeOnly.text}` },
];

export const BILLING_METHODS = [
  { value: "invoice", label: "Emailed invoice, net 30", hint: "The Treasurer gets an invoice by email and pays by card, ACH or check." },
  { value: "card", label: "Card on file, charged automatically", hint: "Needs a card saved below." },
];

// ---------- CSV imports ----------
// ---------- Officer hardware (parking handheld) ----------
// Who pays for officer phones and ticket printers. Saved on customers/{id}.plan.hardware.
// Default is "at cost"; "included" is for closing bigger towns.
export const HARDWARE = {
  printerModel: "Zebra ZQ320 Plus (outdoor, Bluetooth)",
  printerPart: "ZQ32-A0E04T0-00",
  printerCost: 470, // what one printer costs you, in dollars. Update when your price changes.
  includedFromTier: 2, // PRICE_TIERS index from which "Included" is suggested (50,000 to 150,000 and up)
  includedCap: 3, // most printers included with the setup fee
  agreementLine: "Officer phones and printers are purchased by the City, or by Municipal Collectionz at cost on request.",
};

export const HARDWARE_OPTIONS = [
  { value: "at_cost", label: "We order at cost", hint: "Added to the setup invoice at what we pay. Order once it's paid." },
  { value: "city", label: "City buys its own", hint: "We send them the exact printer model to order." },
  { value: "included", label: "Printers included", hint: `Up to ${HARDWARE.includedCap} printers come with the setup fee. Phones are still the city's.` },
  { value: "none", label: "Not needed", hint: "No parking module, or they already have what they need." },
];

export function hardwareLabel(h) {
  const opt = HARDWARE_OPTIONS.find((o) => o.value === (h && h.option));
  return opt ? opt.label : "Not set";
}

// "3 printers and 2 phones" style summary.
export function hardwareCounts(h) {
  if (!h) return "";
  const parts = [];
  if (h.printers) parts.push(`${h.printers} ${h.printers === 1 ? "printer" : "printers"}`);
  if (h.phones) parts.push(`${h.phones} ${h.phones === 1 ? "phone" : "phones"}`);
  return parts.join(" and ");
}

// City colors picked in Settings → Seal & colors (customers/{id}.colorPreference).
// The setup team uses them for the city's resident payment pages and receipts.
export const CITY_THEMES = [
  { value: "green", label: "Civic green", hex: "#1e6b52" },
  { value: "navy", label: "Town hall navy", hex: "#0e2236" },
  { value: "harbor", label: "Harbor blue", hex: "#2b5c8a" },
  { value: "brick", label: "Brick red", hex: "#9b3d2a" },
  { value: "brass", label: "Brass", hex: "#a8752f" },
  { value: "slate", label: "Slate", hex: "#4a5560" },
];

export const BILL_TYPES = ["Real Estate", "Personal Property", "Motor Vehicle Excise", "Water/Sewer", "Trash", "Boat Excise", "Other"];

export const IMPORT_KINDS = {
  bills: {
    label: "Bills (commitment file)",
    short: "Bills",
    icon: "file",
    hint: "One row per bill the city has committed: tax, excise, water and sewer. This is usually exported from your current billing software.",
    columns: ["billType", "billNumber", "accountNumber", "ownerName", "serviceAddress", "amountDue", "dueDate", "fiscalYear"],
    required: ["billType", "billNumber", "ownerName", "amountDue", "dueDate"],
    idColumn: "billNumber",
    amountColumn: "amountDue",
    groupColumn: "billType",
    sample: [
      ["Real Estate", "2027-000184", "R-10442", "Jordan Smith", "14 Elm St", "1842.50", "2026-11-01", "2027"],
      ["Water/Sewer", "WS-55120", "W-2201", "Casey Brown", "9 Pond Rd", "212.40", "2026-11-15", "2027"],
    ],
  },
  parking: {
    label: "Open parking tickets",
    short: "Parking",
    icon: "ticket",
    hint: "Unpaid tickets the city wants residents to be able to pay online on day one.",
    columns: ["ticketNumber", "plate", "plateState", "violation", "issuedDate", "fineAmount", "location"],
    required: ["ticketNumber", "plate", "violation", "issuedDate", "fineAmount"],
    idColumn: "ticketNumber",
    amountColumn: "fineAmount",
    groupColumn: "violation",
    sample: [
      ["P-0048812", "8KTR21", "MA", "Expired meter", "2026-09-30", "25.00", "Main St lot"],
      ["P-0048813", "5XLM90", "NH", "No parking zone", "2026-10-02", "40.00", "Town Hall"],
    ],
  },
  staff: {
    label: "Staff accounts",
    short: "Staff",
    icon: "users",
    hint: "Everyone in your offices who needs a login. We create the accounts during setup, so nobody gets an email yet.",
    columns: ["email", "fullName", "department", "role"],
    required: ["email", "fullName", "role"],
    idColumn: "email",
    groupColumn: "role",
    sample: [
      ["mwalsh@westbrook-ma.gov", "Mary Walsh", "Treasurer/Collector", "Treasurer/Collector"],
      ["jlee@westbrook-ma.gov", "Jordan Lee", "Police", "Parking Clerk"],
    ],
  },
};

export const MAX_IMPORT_ROWS = 25000;

const keyOf = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

// Maps whatever header the city's file uses ("Bill Number", "bill_number") to our column names.
export function canonicalColumns(kind, headers) {
  const cols = IMPORT_KINDS[kind].columns;
  const lookup = Object.fromEntries(cols.map((c) => [keyOf(c), c]));
  const map = {};
  headers.forEach((h) => {
    const c = lookup[keyOf(h)];
    if (c) map[h] = c;
  });
  return map;
}

export function normalizeRows(kind, rows, headers) {
  const map = canonicalColumns(kind, headers);
  return rows.map((r) => {
    const out = {};
    Object.keys(r).forEach((h) => {
      if (map[h]) out[map[h]] = String(r[h] == null ? "" : r[h]).trim();
    });
    return out;
  });
}

export function missingColumns(kind, headers) {
  const have = Object.values(canonicalColumns(kind, headers));
  return IMPORT_KINDS[kind].required.filter((c) => !have.includes(c));
}

export function parseAmount(v) {
  const s = String(v || "").replace(/[$,\s]/g, "");
  if (!/^-?\d+(\.\d{1,2})?$/.test(s)) return null;
  return Number(s);
}

export function parseDate(v) {
  const s = String(v || "").trim();
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return validDate(+m[1], +m[2], +m[3]);
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
  if (m) return validDate(m[3].length === 2 ? 2000 + +m[3] : +m[3], +m[1], +m[2]);
  return null;
}

function validDate(y, mo, d) {
  const dt = new Date(Date.UTC(y, mo - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d ? dt.toISOString().slice(0, 10) : null;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Same checks the importCityCsv function runs. Rows must already be normalized.
export function validateRows(kind, rows) {
  const def = IMPORT_KINDS[kind];
  const seen = new Set();
  const errors = [];
  let valid = 0;
  let totalCents = 0;
  const byGroup = {};

  rows.forEach((r, i) => {
    const problems = [];
    def.required.forEach((c) => {
      if (!r[c]) problems.push(`${c} is empty`);
    });
    const id = keyOf(r[def.idColumn]);
    if (id) {
      if (seen.has(id)) problems.push(`duplicate ${def.idColumn}`);
      seen.add(id);
    }
    let cents = 0;
    if (def.amountColumn && r[def.amountColumn]) {
      const a = parseAmount(r[def.amountColumn]);
      if (a === null || a < 0) problems.push(`${def.amountColumn} is not a dollar amount`);
      else cents = Math.round(a * 100);
    }
    if (kind === "bills") {
      if (r.billType && !BILL_TYPES.some((t) => keyOf(t) === keyOf(r.billType))) problems.push(`billType must be one of: ${BILL_TYPES.join(", ")}`);
      if (r.dueDate && !parseDate(r.dueDate)) problems.push("dueDate must look like 2026-11-01 or 11/01/2026");
    }
    if (kind === "parking") {
      if (r.issuedDate && !parseDate(r.issuedDate)) problems.push("issuedDate must look like 2026-09-30 or 09/30/2026");
      if (r.plateState && !/^[A-Za-z]{2}$/.test(r.plateState)) problems.push("plateState must be a 2-letter state");
    }
    if (kind === "staff") {
      if (r.email && !EMAIL.test(r.email)) problems.push("email is not valid");
      if (r.role && !CITY_STAFF_ROLES.some((t) => keyOf(t) === keyOf(r.role))) problems.push(`role must be one of: ${CITY_STAFF_ROLES.join(", ")}`);
    }

    if (problems.length) {
      errors.push({ row: i, error: problems.join("; ") });
    } else {
      valid += 1;
      totalCents += cents;
      const g = r[def.groupColumn] || "Other";
      byGroup[g] = (byGroup[g] || 0) + 1;
    }
  });

  return { valid, errors, totalCents, byGroup };
}

export function templateCsv(kind) {
  const def = IMPORT_KINDS[kind];
  const lines = [def.columns, ...def.sample].map((r) => r.map((c) => (/[",\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c)).join(","));
  return lines.join("\n") + "\n";
}

export function downloadText(filename, text, type = "text/csv;charset=utf-8;") {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}